import React, { useEffect, useId, useRef, useState } from 'react';
import '../../styles/shopper.css';

type Phase = 'rest' | 'armed' | 'sewn';

interface StitchProps {
  /** When the thread sews itself round: once in view, at once, or never (simply there). */
  sewOn?: 'view' | 'mount' | false;
  /** Change it to sew the thread again (a card turned over, say). */
  sewKey?: string | number;
  className?: string;
}

const reducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * The landing's woven-label stitch (landing/src/components/LabelTag.astro): a
 * dashed orange thread inset from the label's edge, revealed once by a mask
 * whose stroke (the needle) runs round the label. Its parent must be
 * position: relative. With reduced motion, the thread is simply there.
 */
const Stitch: React.FC<StitchProps> = ({ sewOn = 'view', sewKey, className = '' }) => {
  const maskId = `st-${useId().replace(/[^a-zA-Z0-9-]/g, '')}`;
  const ref = useRef<SVGSVGElement>(null);
  const [phase, setPhase] = useState<Phase>('rest');
  const [len, setLen] = useState(600);

  useEffect(() => {
    const svg = ref.current;
    if (!svg || sewOn === false || reducedMotion()) {
      setPhase('rest');
      return;
    }
    const measure = () => {
      // offsetWidth-free: the bar can sit inside a scaled container
      let box: DOMRect | undefined;
      try {
        box = svg.getBBox?.();
      } catch {
        box = undefined; // not rendered yet (Firefox throws)
      }
      const r = svg.getBoundingClientRect();
      const w = box && box.width ? box.width : r.width;
      const h = box && box.height ? box.height : r.height;
      setLen(Math.ceil(2 * (w + h)) + 8);
    };
    measure();
    setPhase('armed');
    let raf = 0;
    const sew = () => {
      measure();
      raf = requestAnimationFrame(() => setPhase('sewn'));
    };
    if (sewOn === 'mount' || typeof IntersectionObserver === 'undefined') {
      const t = window.setTimeout(sew, 120);
      return () => {
        window.clearTimeout(t);
        cancelAnimationFrame(raf);
      };
    }
    const io = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          sew();
          io.disconnect();
        }
      },
      { threshold: 0.6 }
    );
    io.observe(svg.parentElement ?? svg);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [sewOn, sewKey]);

  return (
    <svg
      ref={ref}
      className={`st${phase === 'armed' ? ' is-armed' : phase === 'sewn' ? ' is-armed is-sewn' : ''} ${className}`}
      style={{ '--st-len': `${len}px` } as React.CSSProperties}
      aria-hidden="true"
      focusable="false"
    >
      <mask id={maskId}>
        <rect className="st-needle" width="100%" height="100%" rx="1.5" />
      </mask>
      <rect className="st-base" width="100%" height="100%" rx="1.5" />
      <rect className="st-thread" width="100%" height="100%" rx="1.5" mask={`url(#${maskId})`} />
    </svg>
  );
};

export default Stitch;
